import React, { useState, useMemo } from 'react';
import {
  Modal,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Dimensions,
  SafeAreaView,
} from 'react-native';
import { IEvent, CALENDAR_KEYS } from '@/types/admin/events';
import { ADMIN_COLORS } from '@/constants/admin';
import { formatEventType } from '@/hooks/admin/useEvents';
import { EventInfoTab } from './EventInfoTab';
import { EventRsvpsTab } from './RsvpsTab';
import { EventAttendanceTab } from './AttendanceTab';
import { AdminErrorBoundary } from '@/components/common/admin/mobile/AdminErrorBoundary';
import { SVG_COMPONENTS } from '@/assets/admin';

const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get('window');

interface EventDetailsModalProps {
  visible: boolean;
  event: IEvent | null;
  onClose: () => void;
  onEdit?: (eventId: number) => void;
}

type TabType = 'info' | 'rsvps' | 'attendance';

const TABS: { key: TabType; label: string }[] = [
  { key: 'info', label: 'Event Info' },
  { key: 'rsvps', label: 'RSVPs' },
  { key: 'attendance', label: 'Attendance' },
];

export const EventDetailsModal: React.FC<EventDetailsModalProps> = ({
  visible,
  event,
  onClose,
  onEdit,
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('info');

  const eventType = useMemo(() => {
    if (!event) return null;
    return formatEventType(event);
  }, [event]);

  const eventKey = useMemo(() => {
    if (!eventType) return null;
    return CALENDAR_KEYS.find(key => key.title === eventType);
  }, [eventType]);

  if (!event) return null;

  const renderTabContent = () => {
    switch (activeTab) {
      case 'info':
        return (
          <AdminErrorBoundary context="EventInfoTab">
            <EventInfoTab event={event} />
          </AdminErrorBoundary>
        );
      case 'rsvps':
        return (
          <AdminErrorBoundary context="EventRsvpsTab">
            <EventRsvpsTab event={event} />
          </AdminErrorBoundary>
        );
      case 'attendance':
        return (
          <AdminErrorBoundary context="EventAttendanceTab">
            <EventAttendanceTab event={event} />
          </AdminErrorBoundary>
        );
      default:
        return null;
    }
  };

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={onClose}
    >
      <SafeAreaView style={styles.container}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            <Text style={styles.eventTitle} numberOfLines={2}>
              {event.name}
            </Text>
            <View style={styles.eventStatus}>
              <View
                style={[
                  styles.statusDot,
                  { backgroundColor: eventKey?.color || ADMIN_COLORS.TEXT_SECONDARY }
                ]}
              />
              <Text style={styles.statusText}>{eventType}</Text>
            </View>
          </View>
          <View style={styles.headerButtons}>
            {onEdit && (
              <TouchableOpacity 
                style={styles.editButton} 
                onPress={() => onEdit(event.id)}
                accessibilityLabel="Edit event"
                accessibilityHint="Tap to edit this event"
                accessibilityRole="button"
              >
                <SVG_COMPONENTS.EDIT_DOT width={18} height={18} color={ADMIN_COLORS.TEXT_PRIMARY} />
              </TouchableOpacity>
            )}
            <TouchableOpacity 
              style={styles.closeButton} 
              onPress={onClose}
              accessibilityLabel="Close event details"
              accessibilityHint="Tap to close the event details modal"
              accessibilityRole="button"
            >
              <Text style={styles.closeButtonText}>✕</Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* Tabs */}
        <View style={styles.tabsContainer}>
          {TABS.map((tab) => (
            <TouchableOpacity
              key={tab.key}
              style={[
                styles.tab,
                activeTab === tab.key && styles.activeTab,
              ]}
              onPress={() => setActiveTab(tab.key)}
              accessibilityLabel={`${tab.label} tab`}
              accessibilityHint={`Switch to ${tab.label} view`}
              accessibilityRole="tab"
              accessibilityState={{ selected: activeTab === tab.key }}
            >
              <Text
                style={[
                  styles.tabText,
                  activeTab === tab.key && styles.activeTabText,
                ]}
              >
                {tab.label}
              </Text>
            </TouchableOpacity>
          ))}
        </View>

        {/* Content */}
        <View style={styles.content}>
          {renderTabContent()}
        </View>
      </SafeAreaView>
    </Modal>
  );
};







const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: ADMIN_COLORS.BORDER,
    marginBottom: 10,
  },
  headerLeft: {
    flex: 1,
    marginRight: 16,
  },
  headerButtons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  editButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    justifyContent: 'center',
    alignItems: 'center',
  },
  eventTitle: {
    fontSize: 24,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_PRIMARY,
    marginBottom: 8,
  },
  eventStatus: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginRight: 8,
  },
  statusText: {
    fontSize: 14,
    fontWeight: '500',
    color: ADMIN_COLORS.TEXT_SECONDARY,
  },
  closeButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    justifyContent: 'center',
    alignItems: 'center',
  },
  closeButtonText: {
    fontSize: 18,
    color: ADMIN_COLORS.TEXT_PRIMARY,
  },
  tabsContainer: {
    flexDirection: 'row',
    backgroundColor: ADMIN_COLORS.DATA_TABLE.BACKGROUND,
    marginHorizontal: 20,
    borderRadius: 8,
    padding: 4,
    marginBottom: 20,
  },
  tab: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 6,
    alignItems: 'center',
  },
  activeTab: {
    backgroundColor: ADMIN_COLORS.PRIMARY,
  },
  tabText: {
    fontSize: 14,
    fontWeight: '500',
    color: ADMIN_COLORS.TEXT_SECONDARY,
  },
  activeTabText: {
    color: 'white',
  },
  content: {
    flex: 1,
  },
});
